import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
    CircularProgress,
    IconButton,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Select,
    MenuItem,
    Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ListIcon from '@mui/icons-material/List';
import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PeopleIcon from '@mui/icons-material/People';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { toast } from 'react-toastify';
import ApplicationSettingsRow from './ApplicationSettingsRow';
import { validateApplications, revokeAccess, shareList } from './apihelpers';
import {
    parseCSVFile,
    parseCommaSeparatedInput,
} from './apihelpers';

/**
 * SubscriptionListCard Component
 * Expandable card for managing a subscription list
 */
const SubscriptionListCard = ({
    list,
    isExpanded,
    onToggleExpand,
    onShare,
    onClone,
    onDelete,
    onAddApps,
    onRemoveApp,
    onUpdateAppSettings,
    onSaveAppSettings,
    permission = 'owner',
    graphData,
}) => {
    const [isAddingApps, setIsAddingApps] = useState(false);
    const [inputMethod, setInputMethod] = useState('paste');
    const [bulkInput, setBulkInput] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [validationStatus, setValidationStatus] = useState('idle');
    const [validApps, setValidApps] = useState([]);
    const [invalidApps, setInvalidApps] = useState([]);
    const [expandedApps, setExpandedApps] = useState({});
    const [appChanges, setAppChanges] = useState({});
    const [showSharedUsers, setShowSharedUsers] = useState(false);
    const [permissionChanges, setPermissionChanges] = useState({});
    const [appSettingsLoading, setAppSettingsLoading] = useState({});
    const [appSettings, setAppSettings] = useState({});

    const fileInputRef = useRef(null);
    const canEdit = permission === 'owner' || permission === 'write';
    const isOwner = permission === 'owner';

    const getNotificationSettings = async (applicationName, appId) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/subscription/getSubscriptionSettings`,
                {
                    user_email: graphData?.mail,
                    app_name: applicationName,
                },
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    },
                }
            );
            
            if (response.data.status === 200) {
                console.log("Settings for", applicationName, ":", response.data.settings);
                setAppSettings((prev) => ({
                    ...prev,
                    [appId]: response.data.settings
                }));
                return response.data.settings;
            } else {
                console.error('Failed to fetch notification settings');
                toast.error("Failed to fetch notification settings", {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch notification settings", {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
        }
    };

    const handleToggleAppExpand = async (appId, appName) => {
        const isExpanding = !expandedApps[appId];
        
        setExpandedApps((prev) => ({
            ...prev,
            [appId]: isExpanding,
        }));

        if (isExpanding) {
            setAppSettingsLoading((prev) => ({ ...prev, [appId]: true }));
            await getNotificationSettings(appName, appId);
            setAppSettingsLoading((prev) => ({ ...prev, [appId]: false }));
        }
    };

    const handleAppSettingsChange = (appId, settings) => {
        // Mark that this app has unsaved changes
        setAppChanges((prev) => ({
            ...prev,
            [appId]: true,
        }));

        // Update local settings state
        setAppSettings((prev) => ({
            ...prev,
            [appId]: {
                ...prev[appId],
                ...settings
            }
        }));

        // Notify parent component to update the list state
        onUpdateAppSettings(list.list_id, appId, settings);
    };

    const handleSaveAppSettings = async (app) => {
        try {
            // Get the current application data from settings or list
            const fetchedSettings = appSettings[app.id];
            const currentApp = list.applications?.find((a) => (a.app_id || a.id) === app.id);
            
            if (!currentApp) {
                throw new Error('CI not found in list');
            }

            
            const settingsToSave = fetchedSettings || {
                upstreamSubscribed: currentApp.upstream_subscribed,
                downstreamSubscribed: currentApp.downstream_subscribed,
                notificationType: currentApp.notification_type || 'false',
                subscribedStates: currentApp.subscribed_states || [],
            };

            // Prepare the app data with updated settings
            const appToSave = {
                ...app,
                name: app.name || currentApp.app_name,
                notificationSettings: settingsToSave,
            };

            await onSaveAppSettings(list.list_id, appToSave);
            
            setAppChanges((prev) => ({
                ...prev,
                [app.id]: false,
            }));
        } catch (error) {
            console.error('Error in handleSaveAppSettings:', error);
            toast.error('Failed to save settings', {
                position: 'top-center',
                autoClose: 3000,
            });
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const validExtensions = ['.csv', '.xlsx', '.xls'];
        const hasValidExtension = validExtensions.some((ext) =>
            file.name.toLowerCase().endsWith(ext)
        );

        if (!hasValidExtension) {
            toast.error('Invalid file type. Please upload a CSV or Excel file.', {
                position: 'top-center',
            });
            return;
        }

        setUploadedFile(file);
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
        setValidApps([]);
        setInvalidApps([]);
        setValidationStatus('idle');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleValidate = async () => {
        setValidationStatus('validating');

        try {
            let appNames = [];

            if (inputMethod === 'paste') {
                appNames = parseCommaSeparatedInput(bulkInput);
            } else if (uploadedFile) {
                appNames = await parseCSVFile(uploadedFile);
            }

            if (appNames.length === 0) {
                toast.warning('No CI names found', {
                    position: 'top-center',
                });
                setValidationStatus('idle');
                return;
            }

            const response = await validateApplications(appNames);
            setValidApps(response.valid_applications || []);
            setInvalidApps(response.invalid_applications || []);
            setValidationStatus('preview');

            if (response.invalid_applications?.length > 0) {
                toast.warning(
                    `${response.invalid_applications.length} CI(s) could not be validated`,
                    { position: 'top-center' }
                );
            }
        } catch (error) {
            toast.error('Failed to validate CI', {
                position: 'top-center',
            });
            setValidationStatus('idle');
        }
    };

    const handleAddApps = async () => {
        if (validApps.length === 0) return;

        try {
            await onAddApps(list.list_id, validApps.map((app) => app.app_name));
            toast.success(`Added ${validApps.length} CI(s) to the list`, {
                position: 'top-center',
            });
            handleCancelAddApps();
        } catch (error) {
            toast.error(error.message || 'Failed to add Configuration Items', {
                position: 'top-center',
            });
        }
    };

    const handleCancelAddApps = () => {
        setIsAddingApps(false);
        setBulkInput('');
        setUploadedFile(null);
        setValidApps([]);
        setInvalidApps([]);
        setValidationStatus('idle');
        setInputMethod('paste');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const getPermissionBadge = () => {
        const badges = {
            owner: { text: 'Owner', color: '#4caf50' },
            write: { text: 'Write', color: '#2196f3' },
            read: { text: 'Read Only', color: '#9e9e9e' },
        };
        const badge = badges[permission] || badges.read;
        return (
            <span
                className="permission-badge"
                style={{ backgroundColor: badge.color }}
            >
                {badge.text}
            </span>
        );
    };

    const handlePermissionChange = (userEmail, newPermission) => {
        setPermissionChanges((prev) => ({
            ...prev,
            [userEmail]: newPermission,
        }));
    };

    const handleSavePermissionChange = async (userEmail, currentPermission) => {
        const newPermission = permissionChanges[userEmail];
        if (!newPermission || newPermission === currentPermission) return;

        try {
            const shareWithRead = newPermission === 'read' ? [userEmail] : [];
            const shareWithWrite = newPermission === 'write' ? [userEmail] : [];

            await shareList({
                userName: graphData.displayName,
                userEmail: graphData.mail,
                listId: list.list_id,
                shareWithRead,
                shareWithWrite,
            });

            toast.success(`Permission updated to ${newPermission} for ${userEmail}`);
            
            // Update local state
            setPermissionChanges((prev) => {
                const updated = { ...prev };
                delete updated[userEmail];
                return updated;
            });

            // Trigger parent refresh
            if (onShare) {
                setTimeout(() => window.location.reload(), 1000);
            }
        } catch (error) {
            toast.error(`Failed to update permission: ${error.message}`);
        }
    };

    const handleRevokeAccess = async (userEmail, userName) => {
        if (!window.confirm(`Are you sure you want to revoke access for ${userName || userEmail}?`)) {
            return;
        }

        try {
            await revokeAccess({
                ownerEmail: graphData.mail,
                listId: list.list_id,
                revokeEmails: [userEmail],
            });

            toast.success(`Access revoked for ${userName || userEmail}`);
            
            // Trigger parent refresh
            setTimeout(() => window.location.reload(), 1000);
        } catch (error) {
            toast.error(`Failed to revoke access: ${error.message}`);
        }
    };

    return (
        <div className="subscription-list-card">
            {/* Header */}
            <div className="list-card-header">
                <div
                    className="list-card-header-left"
                    onClick={() => onToggleExpand(list.list_id)}
                >
                    {isExpanded ? (
                        <ExpandLessIcon className="expand-icon" />
                    ) : (
                        <ExpandMoreIcon className="expand-icon" />
                    )}
                    <ListIcon className="list-icon" />
                    <div className="list-info">
                        <div className="list-name-row">
                            <span className="list-name">{list.list_name}</span>
                            {getPermissionBadge()}
                        </div>
                        <span className="list-meta">
                            {list.app_count || list.applications?.length || 0} Configuration Item
                            {(list.app_count || list.applications?.length || 0) !== 1 ? 's' : ''} •
                            Created {formatDate(list.created_at)}
                            {list.owner?.user_name && permission !== 'owner' && (
                                <> • Owner: {list.owner.user_name}</>
                            )}
                        </span>
                    </div>
                </div>

                <div className="list-card-header-right">
                    {canEdit && (
                        <Tooltip title="Add CIs">
                            <button
                                className="action-btn add-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsAddingApps(true);
                                }}
                            >
                                <AddIcon /> Add CIs
                            </button>
                        </Tooltip>
                    )}

                    {isOwner && (
                        <Tooltip title="Share List">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onShare(list);
                                }}
                            >
                                <ShareIcon />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip title="Clone List">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClone(list);
                            }}
                        >
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>

                    {isOwner && (
                        <Tooltip title="Delete List">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(list);
                                }}
                                style={{ color: '#FA6A55' }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            </div>

            {/* Add Apps Form */}
            {isAddingApps && (
                <div className="add-apps-form">
                    <div className="add-apps-header">
                        <h5>Add CIs to {list.list_name}</h5>
                        <IconButton size="small" onClick={handleCancelAddApps}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    {/* Input Method Toggle */}
                    <div className="input-method-toggle">
                        <button
                            className={`toggle-btn ${inputMethod === 'paste' ? 'active' : ''}`}
                            onClick={() => setInputMethod('paste')}
                        >
                            Paste Values
                        </button>
                        <button
                            className={`toggle-btn ${inputMethod === 'upload' ? 'active' : ''}`}
                            onClick={() => setInputMethod('upload')}
                        >
                            Upload File
                        </button>
                    </div>

                    {/* Paste Input */}
                    {inputMethod === 'paste' && (
                        <div className="input-section">
                            <textarea
                                placeholder="Enter CI names separated by commas"
                                value={bulkInput}
                                onChange={(e) => setBulkInput(e.target.value)}
                                className="form-textarea"
                                rows={3}
                            />
                            <button
                                className="animated-search-button validate-btn"
                                onClick={handleValidate}
                                disabled={validationStatus === 'validating' || !bulkInput.trim()}
                            >
                                {validationStatus === 'validating' ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    'Validate'
                                )}
                            </button>
                        </div>
                    )}

                    {/* File Upload */}
                    {inputMethod === 'upload' && (
                        <div className="input-section">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                                id={`add-apps-file-${list.list_id}`}
                            />

                            {!uploadedFile ? (
                                <label
                                    htmlFor={`add-apps-file-${list.list_id}`}
                                    className="file-drop-zone small"
                                >
                                    <CloudUploadIcon className="upload-icon" />
                                    <p>Click to upload file</p>
                                </label>
                            ) : (
                                <div className="uploaded-file">
                                    <div className="file-info">
                                        <InsertDriveFileIcon className="file-icon" />
                                        <span>{uploadedFile.name}</span>
                                    </div>
                                    <IconButton size="small" onClick={handleRemoveFile}>
                                        <CloseIcon />
                                    </IconButton>
                                </div>
                            )}

                            {uploadedFile && (
                                <button
                                    className="animated-search-button validate-btn"
                                    onClick={handleValidate}
                                    disabled={validationStatus === 'validating'}
                                >
                                    {validationStatus === 'validating' ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        'Validate'
                                    )}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Preview */}
                    {validationStatus === 'preview' && validApps.length > 0 && (
                        <div className="add-apps-preview">
                            <p>
                                {validApps.length} valid ci(s) ready to add
                                {invalidApps.length > 0 && (
                                    <span className="invalid-count">
                                        ({invalidApps.length} invalid)
                                    </span>
                                )}
                            </p>
                            <div className="preview-actions">
                                <button
                                    className="animated-search-button cancel-btn"
                                    onClick={handleCancelAddApps}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="animated-search-button add-btn"
                                    onClick={handleAddApps}
                                >
                                    Add {validApps.length} CI{validApps.length !== 1 ? 's' : ''}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Expanded Content - Applications */}
            {isExpanded && (
                <>
                    {/* Shared Users Section - Only for Owners */}
                    {isOwner && (list.shared_with_read?.length > 0 || list.shared_with_write?.length > 0) && (
                        <div className="shared-users-section">
                            <div
                                className="shared-users-header"
                                onClick={() => setShowSharedUsers(!showSharedUsers)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {showSharedUsers ? (
                                        <ExpandLessIcon />
                                    ) : (
                                        <ExpandMoreIcon />
                                    )}
                                    <PeopleIcon />
                                    <strong>
                                        Shared with {(list.shared_with_read?.length || 0) + (list.shared_with_write?.length || 0)} user(s)
                                    </strong>
                                </div>
                            </div>

                            {showSharedUsers && (
                                <div className="shared-users-content">
                                    {/* Read Permission Users */}
                                    {list.shared_with_read && list.shared_with_read.length > 0 && (
                                        <div className="permission-group">
                                            <h5>Read Access</h5>
                                            {list.shared_with_read.map((user) => (
                                                <div key={user.user_email} className="shared-user-item">
                                                    <div className="user-info">
                                                        <span className="user-name">{user.user_name}</span>
                                                        <span className="user-email">{user.user_email}</span>
                                                    </div>
                                                    <div className="user-actions">
                                                        <Select
                                                            value={permissionChanges[user.user_email] || 'read'}
                                                            onChange={(e) => handlePermissionChange(user.user_email, e.target.value)}
                                                            size="small"
                                                            style={{ minWidth: '120px', marginRight: '8px' }}
                                                        >
                                                            <MenuItem value="read">Read</MenuItem>
                                                            <MenuItem value="write">Write</MenuItem>
                                                        </Select>
                                                        {permissionChanges[user.user_email] && 
                                                         permissionChanges[user.user_email] !== 'read' && (
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                onClick={() => handleSavePermissionChange(user.user_email, 'read')}
                                                                style={{ 
                                                                    backgroundColor: '#42b0d5',
                                                                    marginRight: '8px',
                                                                    minWidth: '70px'
                                                                }}
                                                            >
                                                                Save
                                                            </Button>
                                                        )}
                                                        <Tooltip title="Revoke Access">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRevokeAccess(user.user_email, user.user_name)}
                                                                style={{ color: '#FA6A55' }}
                                                            >
                                                                <PersonRemoveIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Write Permission Users */}
                                    {list.shared_with_write && list.shared_with_write.length > 0 && (
                                        <div className="permission-group">
                                            <h5>Write Access</h5>
                                            {list.shared_with_write.map((user) => (
                                                <div key={user.user_email} className="shared-user-item">
                                                    <div className="user-info">
                                                        <span className="user-name">{user.user_name}</span>
                                                        <span className="user-email">{user.user_email}</span>
                                                    </div>
                                                    <div className="user-actions">
                                                        <Select
                                                            value={permissionChanges[user.user_email] || 'write'}
                                                            onChange={(e) => handlePermissionChange(user.user_email, e.target.value)}
                                                            size="small"
                                                            style={{ minWidth: '120px', marginRight: '8px' }}
                                                        >
                                                            <MenuItem value="read">Read</MenuItem>
                                                            <MenuItem value="write">Write</MenuItem>
                                                        </Select>
                                                        {permissionChanges[user.user_email] && 
                                                         permissionChanges[user.user_email] !== 'write' && (
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                onClick={() => handleSavePermissionChange(user.user_email, 'write')}
                                                                style={{ 
                                                                    backgroundColor: '#42b0d5',
                                                                    marginRight: '8px',
                                                                    minWidth: '70px'
                                                                }}
                                                            >
                                                                Save
                                                            </Button>
                                                        )}
                                                        <Tooltip title="Revoke Access">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRevokeAccess(user.user_email, user.user_name)}
                                                                style={{ color: '#FA6A55' }}
                                                            >
                                                                <PersonRemoveIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="list-applications">
                    {list.applications && list.applications.length > 0 ? (
                        list.applications.map((app) => {
                            const appId = app.app_id || app.id;
                            const fetchedSettings = appSettings[appId];
                            
                            return (
                                <ApplicationSettingsRow
                                    key={appId}
                                    application={{
                                        id: appId,
                                        appId: fetchedSettings?.applicationID || app.app_id,
                                        name: app.app_name || app.name,
                                        addedBy: app.added_by,
                                        notificationSettings: fetchedSettings || {
                                            upstreamSubscribed: app.upstream_subscribed,
                                            downstreamSubscribed: app.downstream_subscribed,
                                            notificationType: app.notification_type || 'false',
                                            subscribedStates: app.subscribed_states || [],
                                        },
                                    }}
                                    isExpanded={expandedApps[appId]}
                                    isLoading={appSettingsLoading[appId]}
                                    onToggleExpand={handleToggleAppExpand}
                                    onUpdateSettings={handleAppSettingsChange}
                                    onSave={handleSaveAppSettings}
                                    onRemoveFromList={
                                        canEdit
                                            ? () => onRemoveApp(list.list_id, app.app_name || app.name)
                                            : null
                                    }
                                    permission={permission}
                                    isListContext={true}
                                    hasChanges={appChanges[appId]}
                                />
                            );
                        })
                    ) : (
                        <div className="empty-list">
                            <ListIcon className="empty-icon" />
                            <p>No ci in this list yet.</p>
                            {canEdit && (
                                <p className="empty-hint">
                                    Click "Add CI" to get started.
                                </p>
                            )}
                        </div>
                    )}
                </div>
                </>
            )}
        </div>
    );
};

export default SubscriptionListCard;
